// ---------------------------------
// client/src/pages/UserProfile.jsx
// User profile page
// ---------------------------------
import React, { useEffect, useMemo, useState } from 'react';
import { fetchCurrentUser, updateCurrentUser } from '../utils/users';
import { resolveAssetUrl } from '../utils/assets';
import '../css/userProfile.css';

const TEXT = {
    title: 'פרופיל משתמש',
    description: 'עדכן שם, תפקיד ותמונת פרופיל לשימוש במילוי אוטומטי.',
    name: 'שם מלא',
    occupation: 'תפקיד',
    photo: 'תמונת פרופיל',
    upload: 'העלאת תמונה',
    remove: 'הסרת תמונה',
    save: 'שמירה',
    saving: 'שומר...',
    loading: 'טוען פרופיל...',
    saveSuccess: 'הפרופיל נשמר בהצלחה.',
    saveError: 'נכשל לעדכן פרופיל.',
    invalidImage: 'ניתן להעלות קבצי תמונה בלבד.',
    imageTooLarge: 'גודל התמונה גדול מדי (עד 10MB).',
};

const MAX_PROFILE_IMAGE_BYTES = 10 * 1024 * 1024;

function getInitials(name) {
    if (!name) return '—';
    const parts = name.trim().split(/\s+/);
    const first = parts[0]?.[0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1]?.[0] || '' : '';
    return `${first}${last}` || '—';
}

export default function UserProfile() {
    const [profile, setProfile] = useState({
        name: '',
        occupation: '',
        profileImageUrl: '',
        email: '',
    });
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        const load = async () => {
            try {
                setIsLoading(true);
                const data = await fetchCurrentUser();
                setProfile({
                    name: data?.name || '',
                    occupation: data?.occupation || '',
                    profileImageUrl: data?.profileImageUrl || '',
                    email: data?.email || '',
                });
            } catch (err) {
                setError(err.response?.data?.message || TEXT.saveError);
            } finally {
                setIsLoading(false);
            }
        };
        load();
    }, []);

    const initials = useMemo(() => getInitials(profile.name), [profile.name]);
    const resolvedProfileImage = useMemo(
        () => resolveAssetUrl(profile.profileImageUrl),
        [profile.profileImageUrl],
    );

    const handlePhotoUpload = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        setError('');
        setSuccess('');
        if (!file.type || !file.type.startsWith('image/')) {
            setError(TEXT.invalidImage);
            return;
        }
        if (file.size > MAX_PROFILE_IMAGE_BYTES) {
            setError(TEXT.imageTooLarge);
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            setProfile((prev) => ({
                ...prev,
                profileImageUrl: typeof reader.result === 'string' ? reader.result : prev.profileImageUrl,
            }));
        };
        reader.readAsDataURL(file);
    };

    const handleSave = async () => {
        try {
            setIsSaving(true);
            setError('');
            setSuccess('');
            const updated = await updateCurrentUser({
                name: profile.name.trim(),
                occupation: profile.occupation.trim(),
                profileImageUrl: profile.profileImageUrl,
            });
            setProfile((prev) => ({
                ...prev,
                name: updated?.name || prev.name,
                occupation: updated?.occupation || prev.occupation,
                profileImageUrl: updated?.profileImageUrl || '',
            }));
            setSuccess(TEXT.saveSuccess);
        } catch (err) {
            setError(err.response?.data?.message || TEXT.saveError);
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div className="page page--user-profile page--accent stack">
                <div className="card">{TEXT.loading}</div>
            </div>
        );
    }

    return (
        <div className="page page--user-profile page--accent stack stack--lg">
            <header className="page__header">
                <div className="stack">
                    <h1 className="page__title">{TEXT.title}</h1>
                    <p className="page__description">{TEXT.description}</p>
                </div>
            </header>

            <section className="form-card profile-card">
                <div className="profile-header">
                    <div className="profile-avatar">
                        {resolvedProfileImage ? (
                            <img src={resolvedProfileImage} alt={profile.name} />
                        ) : (
                            <span className="profile-avatar__placeholder">{initials}</span>
                        )}
                    </div>
                    <div className="profile-actions">
                        <label className="button button--light">
                            {TEXT.upload}
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handlePhotoUpload}
                                hidden
                            />
                        </label>
                        <button
                            type="button"
                            className="button button--ghost"
                            onClick={() => setProfile((prev) => ({ ...prev, profileImageUrl: '' }))}
                        >
                            {TEXT.remove}
                        </button>
                    </div>
                </div>

                <label className="form-field">
                    <span className="form-field__label">{TEXT.name}</span>
                    <input
                        className="form-field__control"
                        value={profile.name}
                        onChange={(event) => setProfile((prev) => ({ ...prev, name: event.target.value }))}
                    />
                </label>

                <label className="form-field">
                    <span className="form-field__label">{TEXT.occupation}</span>
                    <input
                        className="form-field__control"
                        value={profile.occupation}
                        onChange={(event) => setProfile((prev) => ({ ...prev, occupation: event.target.value }))}
                    />
                </label>

                {error && <div className="alert alert--danger">{error}</div>}
                {success && <div className="alert alert--success">{success}</div>}

                <div className="form-actions">
                    <span className="card__meta">{profile.email}</span>
                    <button
                        type="button"
                        className="button button--primary"
                        onClick={handleSave}
                        disabled={isSaving}
                    >
                        {isSaving ? TEXT.saving : TEXT.save}
                    </button>
                </div>
            </section>
        </div>
    );
}
